/**
 * useSahibActions — extracted message + tool + plan + journal handlers
 * for SahibCompanion. Pulled out in v167.x to keep the main component
 * under ~200 lines and cut its cyclomatic complexity. Pure side-effect
 * orchestration; all UI state mutators are passed in from the parent.
 */
import { useCallback } from 'react';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import api from '@/lib/api';

const planCueRegex = /\b(plan|sequence|then|after that|and then|and also)\b/i;

function looksLikePlan(text) {
  const cued = planCueRegex.test(text);
  const manyAnds = (text.match(/\band\b/gi) || []).length >= 2;
  return (cued || manyAnds) && text.length > 30;
}

function userTz() {
  return (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || 'UTC';
}

export default function useSahibActions({
  loc, navTo, meta, activePersona, activeMeta, sessions,
  setOpen, setMsg, setHistory, setSessions, setBusy,
  setShotData, setShotBusy, setJournalSent,
  inputRef,
}) {
  // ── Screenshot capture (Iqra journal flow) ──
  const captureScreenshot = useCallback(async () => {
    setShotBusy(true);
    try {
      setOpen(false);
      await new Promise((r) => setTimeout(r, 220));
      const canvas = await html2canvas(document.body, {
        useCORS: true, allowTaint: true, backgroundColor: '#ffffff',
        scale: 0.75,
        windowWidth: document.documentElement.scrollWidth,
        windowHeight: document.documentElement.scrollHeight,
      });
      setShotData(canvas.toDataURL('image/jpeg', 0.7));
      setOpen(true);
      toast.success('Screenshot captured — write a note and send.');
    } catch (e) {
      toast.error('Could not capture screenshot.');
      setOpen(true);
    } finally { setShotBusy(false); }
  }, [setShotBusy, setOpen, setShotData]);

  const sendJournalEntry = useCallback(async (msg, shotData) => {
    if (!msg.trim() && !shotData) {
      toast.error('Add a note or a screenshot first.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/iqra/feedback', {
        user_text: msg.trim() || '(screenshot only)',
        page_url: loc.pathname,
        page_title: meta?.page_label,
        screenshot_data_url: shotData || undefined,
      });
      setJournalSent(true);
      setMsg('');
      setShotData(null);
      toast.success('Added to the Feedback Journal — admin can review now.');
      setTimeout(() => setJournalSent(false), 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not log to journal.');
    } finally { setBusy(false); }
  }, [loc.pathname, meta, setBusy, setJournalSent, setMsg, setShotData]);

  // ── Tool router: try plan → intent → plain chat ──
  const routeAgentic = useCallback(async (userText, pKey) => {
    const planRoute = looksLikePlan(userText);
    if (planRoute) {
      const { data } = await api.post('/iqra/tools/plan', {
        message: userText, page_url: loc.pathname, page_title: meta?.page_label,
      });
      if (data?.handled && Array.isArray(data?.steps) && data.steps.length > 0) {
        setHistory((h) => ({
          ...h,
          [pKey]: [...(h[pKey] || []), {
            role: 'ai',
            text: `I built a plan with ${data.steps.length} step${data.steps.length === 1 ? '' : 's'}.`,
            pendingPlan: {
              plan_id: data.plan_id, steps: data.steps,
              confirmation_text: data.confirmation_text,
              contains_writes: data.contains_writes,
            },
          }],
        }));
        return true;
      }
    }
    const { data } = await api.post('/iqra/tools/intent', {
      message: userText, page_url: loc.pathname, page_title: meta?.page_label,
    });
    if (!data?.handled) {
      if (data?.iqra_reply) {
        setHistory((h) => ({ ...h, [pKey]: [...(h[pKey] || []), { role: 'ai', text: data.iqra_reply }] }));
        return true;
      }
      return false;
    }
    if (data.confirmation_required) {
      setHistory((h) => ({
        ...h,
        [pKey]: [...(h[pKey] || []), {
          role: 'ai',
          text: data.explanation || `I'll run ${data.tool}.`,
          pendingTool: { tool: data.tool, args: data.args, confirmation_text: data.confirmation_text },
        }],
      }));
      return true;
    }
    setHistory((h) => ({
      ...h,
      [pKey]: [...(h[pKey] || []), { role: 'ai', text: data.iqra_reply || 'Done.' }],
    }));
    if (data.tool === 'navigate' && data.args?.path) {
      setTimeout(() => { setOpen(false); navTo(data.args.path); }, 600);
    }
    return true;
  }, [loc.pathname, meta, navTo, setHistory, setOpen]);

  const sendPlainChat = useCallback(async (userText) => {
    try {
      const ctx = `User is on page "${meta?.page_label}" (${loc.pathname}).`;
      const { data } = await api.post('/ai/employees/chat', {
        persona: activePersona,
        message: userText,
        session_id: sessions[activePersona] || undefined,
        tool_context: ctx,
      });
      setSessions((s) => ({ ...s, [activePersona]: data.session_id }));
      setHistory((h) => ({
        ...h,
        [activePersona]: [...(h[activePersona] || []), { role: 'ai', text: data.reply }],
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${activeMeta?.english_name || 'Sahib'} is unreachable.`);
    }
  }, [loc.pathname, meta, activePersona, activeMeta, sessions, setSessions, setHistory]);

  const send = useCallback(async (msg) => {
    if (!msg.trim()) return;
    const userText = msg.trim();
    setMsg('');
    const pKey = activePersona;
    setHistory((h) => ({ ...h, [pKey]: [...(h[pKey] || []), { role: 'user', text: userText }] }));
    setBusy(true);
    const isAgenticPersona = pKey === 'iqra' || pKey === 'yusuf';
    let handled = false;
    if (isAgenticPersona) {
      try { handled = await routeAgentic(userText, pKey); }
      catch (e) { console.warn('Tool router failed, falling back to chat:', e); }
    }
    if (!handled) await sendPlainChat(userText);
    setBusy(false);
    setTimeout(() => inputRef.current?.focus(), 30);
  }, [activePersona, inputRef, routeAgentic, sendPlainChat, setBusy, setHistory, setMsg]);

  // ── Tool confirmation (single-step) ──
  const confirmToolCall = useCallback(async (tool, args, msgIndex, pKey) => {
    setBusy(true);
    try {
      const argsWithTz = { ...(args || {}), tz: (args && args.tz) || userTz() };
      const { data } = await api.post('/iqra/tools/execute', { tool, args: argsWithTz });
      setHistory((h) => {
        const arr = [...(h[pKey] || [])];
        if (arr[msgIndex]) arr[msgIndex] = { ...arr[msgIndex], pendingTool: null, confirmed: true };
        arr.push({ role: 'ai', text: data.iqra_reply || `${tool} done.` });
        return { ...h, [pKey]: arr };
      });
      toast.success(data.iqra_reply || `${tool} executed.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not execute.');
    } finally { setBusy(false); }
  }, [setBusy, setHistory]);

  const declineToolCall = useCallback((msgIndex, pKey, history) => {
    const m = (history[pKey] || [])[msgIndex];
    const tool = m?.pendingTool?.tool;
    const args = m?.pendingTool?.args;
    const confirmation = m?.pendingTool?.confirmation_text;
    setHistory((h) => {
      const arr = [...(h[pKey] || [])];
      if (arr[msgIndex]) arr[msgIndex] = { ...arr[msgIndex], pendingTool: null, declined: true };
      arr.push({ role: 'ai', text: 'Understood — I won\'t run it.' });
      return { ...h, [pKey]: arr };
    });
    if (tool) {
      api.post('/iqra/tools/refusals', {
        tool, args: args || {}, confirmation_text: confirmation, page_url: loc.pathname,
      }).catch(() => { /* silent */ });
    }
  }, [loc.pathname, setHistory]);

  // ── Plan confirmation (multi-step) ──
  const confirmPlan = useCallback(async (planId, msgIndex, pKey, history) => {
    setBusy(true);
    const planMsg = (history[pKey] || [])[msgIndex];
    const steps = planMsg?.pendingPlan?.steps || [];
    setHistory((h) => {
      const arr = [...(h[pKey] || [])];
      if (arr[msgIndex]) {
        arr[msgIndex] = {
          ...arr[msgIndex],
          pendingPlan: null,
          runningPlan: {
            plan_id: planId,
            steps: steps.map((s) => ({ ...s, status: 'queued', result: null, error: null })),
          },
        };
      }
      return { ...h, [pKey]: arr };
    });
    let halted = false;
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      setHistory((h) => {
        const arr = [...(h[pKey] || [])];
        const rp = arr[msgIndex]?.runningPlan;
        if (rp) rp.steps = rp.steps.map((s, j) => j === i ? { ...s, status: 'running' } : s);
        return { ...h, [pKey]: arr };
      });
      try {
        const argsWithTz = { ...(step.args || {}), tz: (step.args && step.args.tz) || userTz() };
        const { data } = await api.post('/iqra/tools/execute', { tool: step.tool, args: argsWithTz });
        setHistory((h) => {
          const arr = [...(h[pKey] || [])];
          const rp = arr[msgIndex]?.runningPlan;
          if (rp) rp.steps = rp.steps.map((s, j) => j === i ? { ...s, status: 'done', result: data.iqra_reply } : s);
          return { ...h, [pKey]: arr };
        });
      } catch (e) {
        halted = true;
        const errMsg = e?.response?.data?.detail || e.message || 'step failed';
        setHistory((h) => {
          const arr = [...(h[pKey] || [])];
          const rp = arr[msgIndex]?.runningPlan;
          if (rp) rp.steps = rp.steps.map((s, j) => j === i ? { ...s, status: 'failed', error: errMsg } : s);
          return { ...h, [pKey]: arr };
        });
        break;
      }
    }
    setHistory((h) => {
      const arr = [...(h[pKey] || [])];
      if (arr[msgIndex]) arr[msgIndex] = { ...arr[msgIndex], planConfirmed: !halted, planHalted: halted };
      arr.push({
        role: 'ai',
        text: halted
          ? 'I halted the plan at the failed step. Earlier steps still ran.'
          : `Plan complete — ${steps.length} step${steps.length === 1 ? '' : 's'} executed.`,
      });
      return { ...h, [pKey]: arr };
    });
    toast.success(halted ? 'Plan halted' : 'Plan completed.');
    setBusy(false);
  }, [setBusy, setHistory]);

  const declinePlan = useCallback((msgIndex, pKey, history) => {
    const m = (history[pKey] || [])[msgIndex];
    const steps = m?.pendingPlan?.steps || [];
    const confirmation = m?.pendingPlan?.confirmation_text;
    setHistory((h) => {
      const arr = [...(h[pKey] || [])];
      if (arr[msgIndex]) arr[msgIndex] = { ...arr[msgIndex], pendingPlan: null, planDeclined: true };
      arr.push({ role: 'ai', text: 'Plan discarded — say the word when you want me to try again.' });
      return { ...h, [pKey]: arr };
    });
    steps.forEach((s) => {
      api.post('/iqra/tools/refusals', {
        tool: s.tool, args: s.args || {},
        confirmation_text: confirmation, page_url: loc.pathname,
      }).catch(() => { /* silent */ });
    });
  }, [loc.pathname, setHistory]);

  return {
    captureScreenshot, sendJournalEntry,
    send, confirmToolCall, declineToolCall, confirmPlan, declinePlan,
  };
}
